import * as React from 'react'
import { Download, Loader2 } from 'lucide-react'
import { downloadNeo4jBackup, useActiveBranchRecord } from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const obj = error as Record<string, unknown>
    if (typeof obj['message'] === 'string') return obj['message']
  }
  return 'Unknown error.'
}

/** Sanitize a branch name for use in a download filename. */
export function neo4jBackupClientFilename(branchName: string, now = new Date()): string {
  const safe = branchName
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, 48)
  const label = safe || 'main'
  const stamp = now
    .toISOString()
    .replace(/[-:]/g, '')
    .replace(/\.\d{3}Z$/, 'Z')
  return `neo4j-${label}-${stamp}.backup`
}

export function Neo4jBackupCard({ embedded = false }: { embedded?: boolean }) {
  const activeBranch = useActiveBranchRecord()
  const branchLabel = activeBranch?.name?.trim() || 'main'

  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [savedAs, setSavedAs] = React.useState('')

  const runDownload = React.useCallback(async () => {
    setError('')
    setSavedAs('')
    const fileName = neo4jBackupClientFilename(branchLabel)
    setBusy(true)
    try {
      await downloadNeo4jBackup(fileName)
      setSavedAs(fileName)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }, [branchLabel])

  const body = (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink-2">Download Neo4j backup</div>
          <p className="m-0 text-11 text-ink-3">
            Online <span className="font-mono">.backup</span> of{' '}
            <span className="font-mono">{branchLabel}</span>
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => void runDownload()}
          disabled={busy}
        >
          {busy ? <Loader2 className="animate-spin" /> : <Download />}
          {busy ? 'Preparing…' : 'Download'}
        </Button>
      </div>

      {error ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-2 py-1 text-11 text-st-down">
          {error}
        </div>
      ) : null}

      {savedAs ? (
        <p className="m-0 text-11 text-ink-2">
          Saved <span className="font-mono">{savedAs}</span>.
        </p>
      ) : null}
    </div>
  )

  if (embedded) {
    return <div className="rounded-xl border bg-card px-4 py-3 shadow-sm">{body}</div>
  }

  return (
    <Card className="gap-0 py-0 shadow-sm">
      <CardContent className="px-4 py-2.5">{body}</CardContent>
    </Card>
  )
}
